import { configureStore, lruMemoize, ReducersMapObject } from '@reduxjs/toolkit';
import storage from 'redux-persist/lib/storage';
import storageSession from 'redux-persist/lib/storage/session';
import {
    FLUSH,
    PAUSE,
    PERSIST,
    persistReducer,
    persistStore,
    PURGE,
    REGISTER,
    REHYDRATE,
} from 'redux-persist';
import { encryptTransform } from 'redux-persist-transform-encrypt';
import authReducer from '../state/auth/authSlice';
import memberReducer from '../state/member/memberSlice';
import generalReducer from '../state/general/generalSlice';
import hypertrackerReducer from '@/state/hypertracker/hypertackerSlice';
import { createSelectorCreator } from 'reselect';
import isEqual from 'lodash/isEqual';
import logger from '@/lib/logger';

export const createDeepEqualSelector = createSelectorCreator(lruMemoize, {
    equalityCheck: isEqual,
    maxSize: 20,
    resultEqualityCheck: isEqual,
});

const migrate = (state, version) => {
    // Legacy migration checks
    if (state && (state.hasOwnProperty('tokens') || state.hasOwnProperty('isAuth'))) {
        return Promise.resolve(state);
    }
    if (state && state._persist && state._persist.version !== version) {
        // Clear removed slices when version changes
        const newState: any = {
            _persist: state._persist,
        };
        // Only keep slices that are still in use
        if (state.auth) newState.auth = state.auth;
        if (state.member) newState.member = state.member;
        if (state.general) newState.general = state.general;
        if (state.hypertracker) newState.hypertracker = state.hypertracker;
        // Update version
        newState._persist.version = version;
        return Promise.resolve(newState);
    }
    if (state && state?.exchangeAccounts && !Array.isArray(state?.exchangeAccounts)) {
        return Promise.resolve({});
    }

    if (state && state?.payments?.discountPercentage && state?.payments?.exchangeAccounts > 11) {
        return Promise.resolve({});
    }

    return Promise.resolve(state);
};

// Increment version to trigger migration and clear removed slices
const version = (__APP_VERSION__ ? +__APP_VERSION__ : -1) + 1;

const hash: string = import.meta.env.REACT_APP_HASH || '';
const env = import.meta.env.REACT_APP_ENVR || 'dev';

const authConfig = {
    key: `@cmm_miniapp_${env}_auth_config`,
    storage,
    migrate,
    version,
};
const memberConfig = {
    key: `@cmm_miniapp_${env}_member_config`,
    storage,
    migrate,
    version,
};

const generalConfig = {
    key: `@cmm_miniapp_${env}_general_config`,
    storage,
    whitelist: ['storedTheme', 'assets', 'countries'],
    migrate,
    version,
};

const hypertrackerConfig = {
    key: `@cmm_miniapp_${env}_hypertracker_config`,
    storage: storageSession,
    migrate,
    version,
    blacklist: ['filters', 'perpsCurrentAsset'],
};

const reducer: ReducersMapObject = {
    auth: persistReducer(
        {
            ...authConfig,
            transforms: [
                encryptTransform({
                    secretKey: hash,
                }),
            ],
        },
        authReducer,
    ),
    member: persistReducer(
        {
            ...memberConfig,
            transforms: [
                encryptTransform({
                    secretKey: hash,
                }),
            ],
        },
        memberReducer,
    ),
    general: persistReducer(generalConfig, generalReducer),
    hypertracker: persistReducer(
        {
            ...hypertrackerConfig,
            transforms: [
                encryptTransform({
                    secretKey: hash,
                }),
            ],
        },
        hypertrackerReducer,
    ),
};

const ignoredActions = [FLUSH, REHYDRATE, PAUSE, PERSIST, PURGE, REGISTER];
const store = configureStore({
    reducer,
    middleware: (getDefaultMiddleware) =>
        getDefaultMiddleware({
            serializableCheck: {
                // prevent errors about serialization (action with functions or persist)
                ignoredActions,
                warnAfter: 128,
            },
            immutableCheck: { warnAfter: 128 },
        }),
    devTools: import.meta.env.MODE !== 'production',
    enhancers: (getDefaultEnhancers) => {
        return getDefaultEnhancers().concat([logger.getReduxEnhancer()]);
    },
});

/* eslint-disable */
export const persistor = persistStore(store);
export type RootState = ReturnType<typeof store.getState>;
export default store;
